import { createSlice } from "@reduxjs/toolkit";
import { getNodeById, getAllNodes, updateNode, getNodesByAccessPointUid, } from "../thunk/nodes-thunk";
const initialState = {
    nodes: null,
    paginationInfo: null,
    selectedNode: null,
    loading: false,
    error: null,
    singleLoading: false,
    singleError: null,
    updateLoading: false,
    updateError: null,
    filters: {
        globalSearch: undefined,
    },
    currentPageNumber: 1,
    currentPageSize: 25,
    sortColumn: null,
    sortDirection: null,
};
const nodesSlice = createSlice({
    name: "nodes",
    initialState,
    reducers: {
        setSelectedNode: (state, action) => {
            state.selectedNode = action.payload;
        },
        clearError: (state) => {
            state.error = null;
            state.singleError = null;
            state.updateError = null;
        },
        clearNodes: (state) => {
            state.nodes = null;
            state.paginationInfo = null;
            state.selectedNode = null;
        },
        updateFilters: (state, action) => {
            // Merge new filters, removing any empty values
            const newFilters = Object.assign({}, state.filters);
            Object.entries(action.payload).forEach(([key, value]) => {
                const isEmptyArray = Array.isArray(value) && value.length === 0;
                const isEmptyValue = value === "" || value === null || value === undefined;
                if (isEmptyArray || isEmptyValue) {
                    delete newFilters[key];
                }
                else {
                    newFilters[key] = value;
                }
            });
            state.filters = newFilters;
            state.currentPageNumber = 1; // Reset to first page on filter change
        },
        setGlobalSearch: (state, action) => {
            state.filters.globalSearch = action.payload;
            state.currentPageNumber = 1;
        },
        setPagination: (state, action) => {
            state.currentPageNumber = action.payload.pageNumber;
            state.currentPageSize = action.payload.pageSize;
        },
        setPageNumber: (state, action) => {
            state.currentPageNumber = action.payload;
        },
        setPageSize: (state, action) => {
            state.currentPageSize = action.payload;
        },
        setSorting: (state, action) => {
            state.sortColumn = action.payload.sortColumn;
            state.sortDirection = action.payload.sortDirection;
        },
        resetFiltersAndPagination: (state) => {
            state.filters = { globalSearch: undefined };
            state.currentPageNumber = 1;
            state.currentPageSize = 25;
            state.sortColumn = null;
            state.sortDirection = null;
        },
        addNodeToList: (state, action) => {
            if (state.nodes) {
                // Check if node already exists
                const existingIndex = state.nodes.findIndex((node) => node.id === action.payload.id ||
                    node.nodeUid === action.payload.nodeUid);
                if (existingIndex === -1) {
                    // Add to the beginning of the list
                    state.nodes.unshift(action.payload);
                    // Update pagination total count
                    if (state.paginationInfo) {
                        state.paginationInfo.totalCount += 1;
                    }
                }
            }
        },
        updateNodeInList: (state, action) => {
            var _a, _b;
            if (state.nodes) {
                const index = state.nodes.findIndex((node) => node.id === action.payload.id ||
                    node.nodeUid === action.payload.nodeUid);
                if (index !== -1) {
                    state.nodes[index] = action.payload;
                }
            }
            // Also update selected node if it matches
            if (((_a = state.selectedNode) === null || _a === void 0 ? void 0 : _a.id) === action.payload.id ||
                ((_b = state.selectedNode) === null || _b === void 0 ? void 0 : _b.nodeUid) === action.payload.nodeUid) {
                // Map NodesDto to NodesUpdateDto for selectedNode
                state.selectedNode = Object.assign(Object.assign({}, action.payload), { doorAlaramDelay: state.selectedNode.doorAlaramDelay, tempAlaramDelay: state.selectedNode.tempAlaramDelay, installationDate: state.selectedNode.installationDate, nodeTypeTwoConfig: state.selectedNode.nodeTypeTwoConfig, nodeTypeOneConfig: state.selectedNode.nodeTypeOneConfig, alaramSettings: state.selectedNode.alaramSettings });
            }
        },
    },
    extraReducers: (builder) => {
        // Get all nodes
        builder
            .addCase(getAllNodes.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
            .addCase(getAllNodes.fulfilled, (state, action) => {
            state.loading = false;
            state.nodes = action.payload.items;
            state.paginationInfo = {
                totalCount: action.payload.totalCount,
                pageNumber: action.payload.pageNumber,
                pageSize: action.payload.pageSize,
                totalPages: action.payload.totalPages,
                hasPreviousPage: action.payload.hasPreviousPage,
                hasNextPage: action.payload.hasNextPage,
            };
        })
            .addCase(getAllNodes.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });
        // Get single node by id
        builder
            .addCase(getNodeById.pending, (state) => {
            state.singleLoading = true;
            state.singleError = null;
        })
            .addCase(getNodeById.fulfilled, (state, action) => {
            state.singleLoading = false;
            state.selectedNode = action.payload;
        })
            .addCase(getNodeById.rejected, (state, action) => {
            state.singleLoading = false;
            state.singleError = action.payload;
        });
        // Update node
        builder
            .addCase(updateNode.pending, (state) => {
            state.updateLoading = true;
            state.updateError = null;
        })
            .addCase(updateNode.fulfilled, (state, action) => {
            state.updateLoading = false;
            state.selectedNode = action.payload;
            // Update the node in the list if it exists
            if (state.nodes) {
                const index = state.nodes.findIndex((node) => node.id === action.payload.id);
                if (index !== -1) {
                    state.nodes[index] = action.payload;
                }
            }
        })
            .addCase(updateNode.rejected, (state, action) => {
            state.updateLoading = false;
            state.updateError = action.payload;
        });
        // Get nodes by access point UID
        builder
            .addCase(getNodesByAccessPointUid.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
            .addCase(getNodesByAccessPointUid.fulfilled, (state, action) => {
            state.loading = false;
            state.nodes = action.payload.items;
            state.paginationInfo = {
                totalCount: action.payload.totalCount,
                pageNumber: action.payload.pageNumber,
                pageSize: action.payload.pageSize,
                totalPages: action.payload.totalPages,
                hasPreviousPage: action.payload.hasPreviousPage,
                hasNextPage: action.payload.hasNextPage,
            };
        })
            .addCase(getNodesByAccessPointUid.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });
    },
});
export const { setSelectedNode, clearError, clearNodes, updateFilters, setGlobalSearch, setPagination, setPageNumber, setPageSize, setSorting, addNodeToList, updateNodeInList, resetFiltersAndPagination, } = nodesSlice.actions;
export default nodesSlice.reducer;
